import React, { Suspense, lazy, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { MarketingHeader } from '../components/MarketingHeader';
import { Check, Zap } from 'lucide-react';
import { pricingAPI } from '../lib/api';

const Footer = lazy(() => import('../components/Footer'));

// Default features fallback
const ALL_FEATURES = [
  'Dashboard',
  'Staff Management',
  'Time Clock',
  'Timesheets',
  'Smart Scheduling',
  'Shift Swaps',
  'Shift Replacement Workflow',
  'Leave Management',
  'Coverage Health',
  'Approval Pipelines',
  'Task Management',
  'Announcements',
  'Notifications',
  'Secure Documents',
  'Documents & Acknowledgements',
  'Payroll Summary',
  'Estimated Pay Preview',
  'Overtime Tracking',
  'Holiday Tracking',
  'Compliance Tracking',
  'Break Compliance Tracking',
  'Labor Cost Dashboard',
  'Labor vs Productivity Dashboard',
  'Revenue Per Labor Hour (RPLH)',
  'Labor Budget Target',
  'Executive Insights',
  'Workforce Analytics',
  'Advanced Reports',
  'Reports & CSV Export',
  'EVA AI Assistant',
  'AI Operational Insights',
  'AI Recommendations',
  'AI Announcement Commands',
  'Smart Digests',
  'Shift Risk Detection',
  'Shift Type Intelligence',
  'Billing & Subscription Control',
  'Mobile App Experience',
  'Mobile Quick Clock',
  'GPS-ready Clock Logic',
  'App Installation',
  'Feature Requests',
  'Support Access',
  'Settings & Permissions',
  'Roles & Permissions',
  'Unlimited Employees',
  'Unlimited Scheduling',
  '30-day Free Trial Included',
];

// Default pricing fallback (used while loading or on error)
const DEFAULT_PLANS = [
  {
    key: 'single',
    name: 'Single Location',
    monthlyPrice: 59,
    yearlyMonthly: 29,
    yearlyTotal: 348,
    badge: null,
    desc: 'Perfect for one location with a growing team.',
    highlight: false,
    features: ALL_FEATURES,
  },
  {
    key: 'multi',
    name: 'Multi Location',
    monthlyPrice: 119,
    yearlyMonthly: 59,
    yearlyTotal: 708,
    badge: 'Best Value',
    desc: 'Built for businesses managing multiple locations.',
    highlight: true,
    features: [...ALL_FEATURES, 'Multi-location Management'],
  },
];

const FAQ = [
  {
    q: 'Is there a free trial?',
    a: 'Yes — every plan includes a 30-day free trial. Cancel anytime, no questions asked.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Absolutely. You can cancel your subscription at any time from your billing settings, with no cancellation fees.',
  },
  {
    q: 'What happens when my trial ends?',
    a: 'You\'ll be prompted to add a payment method. If you don\'t, your account moves to a read-only state — your data is safe.',
  },
  {
    q: 'Does annual billing auto-renew?',
    a: 'Yes, annual plans renew each year. You\'ll receive an email reminder 14 days before renewal.',
  },
  {
    q: 'Can I upgrade or downgrade my plan?',
    a: 'Yes. You can switch plans at any time from the Billing section of your dashboard.',
  },
];

export default function Pricing() {
  const [annual, setAnnual] = useState(false);
  const [plans, setPlans] = useState(DEFAULT_PLANS);

  // Fetch pricing from backend (single source of truth)
  useEffect(() => {
    pricingAPI.getPricing()
      .then(({ data }) => {
        const backendPlans = Object.values(data.plans).map((p) => ({
          key: p.key,
          name: p.name,
          monthlyPrice: p.monthly_price,
          yearlyMonthly: p.yearly_monthly_equivalent,
          yearlyTotal: p.yearly_price,
          badge: p.badge || null,
          desc: p.description,
          highlight: p.key === 'multi',
          features: p.features,
        }));
        // Sort: single first, then multi
        backendPlans.sort((a, b) => (a.key === 'single' ? -1 : 1));
        setPlans(backendPlans);
      })
      .catch(() => {
        // Keep default plans on error
      });
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <MarketingHeader />

      <main>
        {/* Hero */}
        <section className="py-20 px-4 text-center bg-white border-b border-slate-100">
          <div className="max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-full px-4 py-1.5 mb-6">
              <Zap size={14} className="text-orange-500" />
              <span className="text-xs font-semibold text-orange-700 uppercase tracking-wide">30-Day Free Trial</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
              Simple, transparent pricing
            </h1>
            <p className="mt-4 text-lg text-slate-600">
              No setup fees. No contracts. Cancel anytime.
            </p>

            {/* Billing toggle */}
            <div className="mt-8 inline-flex items-center gap-3 bg-slate-100 rounded-full p-1">
              <button
                onClick={() => setAnnual(false)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  !annual ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setAnnual(true)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  annual ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                Annual
                <span className="ml-2 text-xs text-green-600 font-semibold">Save 50%</span>
              </button>
            </div>
          </div>
        </section>

        {/* Plan cards */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto grid sm:grid-cols-2 gap-6">
            {plans.map((plan) => (
              <div
                key={plan.key}
                className={`rounded-2xl border p-8 flex flex-col ${
                  plan.highlight
                    ? 'bg-slate-900 border-slate-700 text-white shadow-xl shadow-slate-900/30'
                    : 'bg-white border-slate-200 text-slate-900'
                }`}
              >
                {plan.badge && (
                  <span className="self-start text-xs font-bold uppercase tracking-widest bg-orange-500 text-white px-3 py-1 rounded-full mb-4">
                    {plan.badge}
                  </span>
                )}
                <h2 className={`text-xl font-bold ${plan.highlight ? 'text-white' : 'text-slate-900'}`}
                    style={{ fontFamily: 'Manrope' }}>
                  {plan.name}
                </h2>
                <p className={`mt-1 text-sm ${plan.highlight ? 'text-slate-400' : 'text-slate-500'}`}>{plan.desc}</p>

                <div className="mt-6">
                  <span className="text-4xl font-extrabold" style={{ fontFamily: 'Manrope' }}>
                    ${annual ? plan.yearlyMonthly : plan.monthlyPrice}
                  </span>
                  <span className={`text-sm ml-1 ${plan.highlight ? 'text-slate-400' : 'text-slate-500'}`}>/month</span>
                  {annual && (
                    <p className={`text-xs mt-1 ${plan.highlight ? 'text-slate-400' : 'text-slate-500'}`}>
                      Billed annually (${plan.yearlyTotal}/yr)
                    </p>
                  )}
                </div>

                {/* Compact differentiator badges — scannable above the bullet list */}
                <div
                  className="mt-5 flex flex-wrap gap-1.5"
                  data-testid={`pricing-badges-${plan.key}`}
                >
                  {[
                    'AI Workforce Insights',
                    'Payroll Automation',
                    'Compliance Tracking',
                    'Multi-location Ready',
                  ].map((label) => (
                    <span
                      key={label}
                      className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${
                        plan.highlight
                          ? 'bg-orange-500/15 text-orange-200 border-orange-400/40'
                          : 'bg-orange-50 text-orange-700 border-orange-200'
                      }`}
                    >
                      <Check size={11} strokeWidth={3} className="flex-shrink-0" />
                      {label}
                    </span>
                  ))}
                </div>

                <ul className="mt-6 space-y-2.5 flex-1">
                  {(plan.features || ALL_FEATURES).map((feat) => (
                    <li key={feat} className="flex items-center gap-2 text-sm">
                      <Check size={15} className="text-orange-500 flex-shrink-0" />
                      <span className={plan.highlight ? 'text-slate-300' : 'text-slate-700'}>{feat}</span>
                    </li>
                  ))}
                </ul>

                <Link to="/register" className="mt-8 block">
                  <Button
                    className={`w-full ${
                      plan.highlight
                        ? 'bg-orange-500 hover:bg-orange-600 text-white'
                        : 'bg-slate-900 hover:bg-slate-800 text-white'
                    }`}
                  >
                    Start Free Trial
                  </Button>
                </Link>
              </div>
            ))}
          </div>

          <p className="text-center text-sm text-slate-500 mt-8">
            All plans include a 30-day free trial. Cancel anytime, no questions asked.
          </p>
        </section>

        {/* FAQ */}
        <section className="py-16 px-4 bg-white border-t border-slate-100">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-slate-900 text-center mb-10" style={{ fontFamily: 'Manrope' }}>
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              {FAQ.map(({ q, a }) => (
                <div key={q} className="border-b border-slate-100 pb-6 last:border-0">
                  <h3 className="font-semibold text-slate-900 mb-2">{q}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA banner */}
        <section className="py-16 px-4 bg-orange-500">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
              Ready to get started?
            </h2>
            <p className="mt-3 text-orange-100">Try ReStaff free for 30 days — no credit card needed.</p>
            <Link to="/register" className="mt-6 inline-block">
              <Button className="bg-white text-orange-600 hover:bg-orange-50 font-semibold px-8">
                Start Your Free Trial
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Suspense fallback={null}>
        <Footer />
      </Suspense>
    </div>
  );
}
